// Orders: aanbiedingen maken, deadlines bepalen, temperatuur en schade bijhouden en de levering scoren.
// Alles hier is pure logica zonder DOM of canvas, zodat het met tests te bewaken is.
import { PRODUCTS, type Product } from './products';
import { detourPx, planRoute, routeLength, type RouteOptions } from './route';
import { DISTRICT_PAY, type BoxId, type VehicleId, coldLossFactor, shockFactor, warmLossFactor } from './economy';

export interface Destination {
  id: string;
  name: string;
  x: number;
  y: number;
  r: number;
  districtId: string;
}

/** 'verloren': Sjors van Knus Expres was eerder bij de klant. */
export type OrderStatus = 'aangeboden' | 'geaccepteerd' | 'ingeladen' | 'bezorgd' | 'verlopen' | 'mislukt' | 'verloren';

export interface Order {
  id: string;
  product: Product;
  dest: Destination;
  customer: string;
  status: OrderStatus;
  /** Shifttijd (s) waarop het aanbod vervalt als je het niet accepteert */
  offerExpiresAt: number;
  /** Seconden vanaf inladen tot de klant de bestelling wil hebben */
  deadlineSec: number;
  /** Shifttijd (s) van inladen */
  loadedAt: number | null;
  /** Temperatuurkwaliteit 0–100 (warm blijven of koud blijven) */
  temp: number;
  /** Gaafheid 0–100 */
  integrity: number;
  /** Verwachte basisvergoeding inclusief wijktoeslag, voor in het aanbod */
  expectedPay: number;
  /** Sjors wil deze klant ook: wie het eerst is, krijgt de bestelling */
  race?: boolean;
}

export type Rng = () => number;

/** Kleine, snelle seeded random (mulberry32), voor tests en later de dagelijkse uitdaging. */
export function seededRng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const OFFER_LIFETIME_SEC = 25;
export const BRIDE_OFFER_LIFETIME_SEC = 30;

/** "Familie Jansen (Croissants)" wordt "Familie Jansen". */
export const customerName = (destName: string) => destName.replace(/\s*\(.*\)\s*$/, '').trim();

/**
 * Deadline in seconden vanaf inladen: vaste marge plus rijtijd over de route (via de brug als het moet).
 * Gekalibreerd met de testrijder (pnpm calibrate): die haalt zonder obstakels ~180 px/s; op rang 1
 * krijg je zo'n 2 tot 3 keer die ideale tijd, en per rang wordt het 10% krapper.
 */
export function deadlineFor(
  product: Product,
  bakery: { x: number; y: number },
  dest: { x: number; y: number },
  rank = 1,
  routeOpts: RouteOptions = {},
): number {
  const base = 10 + routeLength(planRoute(bakery, dest, routeOpts)) / 110;
  const rankFactor = 1 - 0.1 * Math.min(2, Math.max(0, rank - 1));
  return Math.round(base * (product.deadlineFactor ?? 1) * rankFactor);
}

/**
 * Hoe vaak een klant wordt gekozen, op basis van de lengte van de rit: korte ritten komen vaker voor
 * (meer leveringen per shift, minder leeg rijden), verre klanten blijven mogelijk en lonen via de wijktoeslag.
 * Een rit van 600 px weegt ongeveer 2,6 keer zo zwaar als een van 4000 px.
 */
export function destinationWeight(routePx: number): number {
  return 1 / (1 + routePx / 1500);
}

export function expectedPay(product: Product, dest: Destination): number {
  return round2(product.basePay * (DISTRICT_PAY[dest.districtId] ?? 1));
}

let orderSeq = 0;
/** Na het hervatten van een shift mogen nieuwe orders geen id krijgen dat al bestaat. */
export function reserveOrderIds(upTo: number) {
  if (upTo > orderSeq) orderSeq = upTo;
}

export interface OfferContext {
  rng: Rng;
  rank: number;
  districts: string[];
  destinations: Destination[];
  bakery: { x: number; y: number };
  now: number;
  /** Bestemmingen die al in een aanbod of lopende order zitten */
  busyDestinationIds: Set<string>;
  /** Afgesloten straten en de stand van de brug: de rit en de deadline houden daar rekening mee */
  routeOptions?: RouteOptions;
}

/** Kiest een klant met een kans die afneemt met de afstand. */
export function pickDestination(candidates: Destination[], bakery: { x: number; y: number }, rng: Rng, opts: RouteOptions = {}): Destination {
  const weights = candidates.map((d) => destinationWeight(routeLength(planRoute(bakery, d, opts))));
  let r = rng() * weights.reduce((s, w) => s + w, 0);
  for (let i = 0; i < candidates.length; i++) {
    r -= weights[i];
    if (r <= 0) return candidates[i];
  }
  return candidates[candidates.length - 1];
}

/** Maakt een nieuw aanbod, of null als er geen vrije bestemming is. */
export function createOffer(ctx: OfferContext, forcedProduct?: Product): Order | null {
  const candidates = ctx.destinations.filter(
    (d) => d.id !== 'hq' && ctx.districts.includes(d.districtId) && !ctx.busyDestinationIds.has(d.id),
  );
  if (!candidates.length) return null;
  const product = forcedProduct ?? pickProduct(ctx.rng, ctx.rank);
  const dest = pickDestination(candidates, ctx.bakery, ctx.rng, ctx.routeOptions);
  return {
    id: `o${++orderSeq}`,
    product,
    dest,
    customer: customerName(dest.name),
    status: 'aangeboden',
    offerExpiresAt: ctx.now + (product.fillsBox ? BRIDE_OFFER_LIFETIME_SEC : OFFER_LIFETIME_SEC),
    deadlineSec: deadlineFor(product, ctx.bakery, dest, ctx.rank, ctx.routeOptions),
    loadedAt: null,
    temp: 100,
    integrity: 100,
    expectedPay: expectedPay(product, dest),
  };
}

export function pickProduct(rng: Rng, rank: number): Product {
  const pool = PRODUCTS.filter((p) => p.minRank <= rank && p.weight > 0);
  const total = pool.reduce((s, p) => s + p.weight, 0);
  let r = rng() * total;
  for (const p of pool) {
    r -= p.weight;
    if (r <= 0) return p;
  }
  return pool[pool.length - 1];
}

export interface CarryContext {
  box: BoxId;
  vehicle: VehicleId;
  weather: 'sunny' | 'rain' | 'mist';
}

/** Werkt temperatuur bij van een ingeladen order. */
export function tickOrder(order: Order, dt: number, ctx: CarryContext): void {
  const p = order.product;
  if (p.heat === 'geen' || p.tempLossPerSec <= 0) return;
  let factor: number;
  if (p.heat === 'warm') {
    factor = warmLossFactor(ctx.box, ctx.vehicle) * (ctx.weather === 'rain' ? 1.25 : ctx.weather === 'mist' ? 1.1 : 1);
  } else {
    factor = coldLossFactor(ctx.box, ctx.vehicle) * (ctx.weather === 'sunny' ? 1.15 : 1);
  }
  order.temp = Math.max(0, order.temp - p.tempLossPerSec * factor * dt);
}

/** Schade door een schok. Geeft het verlies in procentpunten terug. */
export function applyShock(order: Order, severity: number, box: BoxId, vehicle: VehicleId): number {
  const raw = severity * 12 * order.product.sensitivity * shockFactor(box, vehicle);
  const damage = Math.max(1, Math.min(40, Math.round(raw)));
  order.integrity = Math.max(0, order.integrity - damage);
  return damage;
}

export interface DeliveryResult {
  timeScore: number;
  tempScore: number | null;
  careScore: number;
  score: number;
  stars: number;
  late: boolean;
  pay: number;
  tip: number;
  /** Uitbetaling in euro's (vergoeding plus fooi) */
  total: number;
}

/** Score 0–100 voor stiptheid: vol op tijd, daarna lineair naar 0 bij een halve deadline te laat. */
export function timeScore(elapsed: number, deadline: number): number {
  if (elapsed <= deadline) return 100;
  const late = elapsed - deadline;
  return Math.max(0, Math.round(100 - (late / (deadline * 0.5)) * 100));
}

export function starsFor(score: number): number {
  if (score >= 90) return 5;
  if (score >= 75) return 4;
  if (score >= 55) return 3;
  if (score >= 35) return 2;
  return 1;
}

/**
 * Scoort een levering.
 * @param combo aantal leveringen op rij met 4 of 5 sterren vóór deze levering
 */
export function scoreDelivery(order: Order, now: number, combo: number): DeliveryResult {
  const p = order.product;
  const elapsed = now - (order.loadedAt ?? now);
  const t = timeScore(elapsed, order.deadlineSec);
  const tempCounts = p.heat !== 'geen' && p.weights.temp > 0;
  const temp = tempCounts ? Math.round(order.temp) : null;
  const care = Math.round(order.integrity);

  const wTime = p.weights.time;
  const wTemp = tempCounts ? p.weights.temp : 0;
  const wCare = p.weights.care;
  const score = Math.round((t * wTime + (temp ?? 0) * wTemp + care * wCare) / (wTime + wTemp + wCare));
  const stars = starsFor(score);

  const pay = round2(order.expectedPay * (0.6 + 0.4 * (score / 100)));
  const comboBoost = 1 + 0.1 * Math.min(Math.max(combo, 0), 5);
  const tip = round2(Math.round(p.maxTip * (score / 100) ** 2 * comboBoost * 10) / 10);

  return { timeScore: t, tempScore: temp, careScore: care, score, stars, late: elapsed > order.deadlineSec, pay, tip, total: round2(pay + tip) };
}

/**
 * Een straat gaat dicht terwijl je onderweg bent: je krijgt de extra rijtijd van de omweg erbij,
 * zodat een marktdag nooit een bestelling onhaalbaar maakt. Geeft de extra seconden terug.
 */
export function extendForDetour(order: Order, from: { x: number; y: number }, routeOpts: RouteOptions): number {
  const extra = detourPx(from, order.dest, routeOpts);
  if (extra <= 0) return 0;
  const seconds = Math.ceil(extra / 110);
  order.deadlineSec += seconds;
  return seconds;
}

/** Resterende tijd tot de deadline, in seconden (negatief als te laat). */
export const timeLeft = (order: Order, now: number) => order.deadlineSec - (now - (order.loadedAt ?? now));

export function round2(n: number) {
  return Math.round(n * 100) / 100;
}
